import {
  GetHotelsParams,
  HotelApproveResponse,
  HotelDetailResponse,
  HotelApprovalRequest,
  HotelApprovalResponse,
} from "@/types/admin/hotel-approve-type";
import {
  HotelRateSettings,
  HotelRateSettingsCreate,
  HotelRateSettingsUpdate,
} from "@/types/admin/hotel-rate-settings";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://www.lilylanka.com";

export const propertyReviewService = {
  async getHotelRateSettings(
    hotelId: number,
    baseValue: number = 100,
  ): Promise<HotelRateSettings> {
    const url = `${API_BASE_URL}/api/HotelRateSettings/hotel/${hotelId}/base_value/${baseValue}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch hotel rate settings: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.result;
  },

  async createHotelRateSettings(
    data: HotelRateSettingsCreate,
  ): Promise<HotelRateSettings> {
    const url = `${API_BASE_URL}/api/HotelRateSettings/create`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to create hotel rate settings: ${response.statusText}`,
      );
    }

    return response.json();
  },

  async updateHotelRateSettings(
    id: number,
    data: HotelRateSettingsUpdate,
  ): Promise<HotelRateSettings> {
    const url = `${API_BASE_URL}/api/HotelRateSettings/update/${id}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to update hotel rate settings: ${response.statusText}`,
      );
    }

    return response.json();
  },
  async getApprovedHotels(
    params: {
      status?: number;
      page?: number;
      pageSize?: number;
      sortColumn?: string;
      searchKeyword?: string;
      sortOrder?: string;
    } = {},
  ): Promise<any> {
    const queryParams = new URLSearchParams();

    if (params.status !== undefined)
      queryParams.append("Status", params.status.toString());
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params.sortColumn) queryParams.append("sortColumn", params.sortColumn);
    if (params.searchKeyword)
      queryParams.append("searchKeyword", params.searchKeyword);
    if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const url = `${API_BASE_URL}/api/HotelApprovel/approved?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch approved hotels: ${response.statusText}`,
      );
    }

    return response.json();
  },

  async getHotels(params: GetHotelsParams): Promise<HotelApproveResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page.toString());
    if (params.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params.sortColumn) queryParams.append("sortColumn", params.sortColumn);
    if (params.searchKeyword)
      queryParams.append("searchKeyword", params.searchKeyword);
    if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
    if (params.status !== undefined)
      queryParams.append("status", params.status.toString());

    const url = `${API_BASE_URL}/Hotels?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch hotels: ${response.statusText}`);
    }

    return response.json();
  },

  async getHotelById(id: number): Promise<HotelDetailResponse> {
    const url = `${API_BASE_URL}/api/HotelApprovel/${id}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch hotel details: ${response.statusText}`);
    }

    return response.json();
  },

  async approveHotel(
    id: number,
    approvalData: HotelApprovalRequest,
  ): Promise<HotelApprovalResponse> {
    const url = `${API_BASE_URL}/api/HotelApprovel/${id}/approve`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(approvalData),
    });

    if (!response.ok) {
      throw new Error(`Failed to approve hotel: ${response.statusText}`);
    }

    return response.json();
  },
  
  async setHotelPendingStatus(
  hotelId: number,
  data: { status: number; reason: string },
): Promise<any> {
  const url = `${API_BASE_URL}/api/PartnerHotel/${hotelId}/status`;
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to update hotel status: ${response.statusText}`);
  }

  return response.json();
},
async getHotelStatus(hotelId: number): Promise<{ hotelId: number; hotelName: string; status: number; reason: string }> {
  const url = `${API_BASE_URL}/api/PartnerHotel/${hotelId}/status`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch hotel status: ${response.statusText}`);
  }

  return response.json();
},
};


